/**
 * The navigation.tsx centralizes navigation logic between screens.
 *
 * Solar Form has multiple question screens, they can combine to make a flow.
 * Moving the navigation logic into component level requires more maintenance effort.
 */

import React from "react";

import { screenNames } from "@/constants";
import { BatteryOutlook } from "@/features/BatteryOutlook/BatteryOutlook";
import { SolarBatteryOutlook } from "@/features/SolarBatteryOutlook/SolarBatteryOutlook";
import { ContactDetail } from "@/features/SolarForm/ContactDetail";
import { SolarGeneration } from "@/features/SolarForm/SolarGeneration";

import { AverageBill } from "./AverageBill";
import { Confirmation } from "./Confirmation";
import { CustomerContact } from "./CustomerContact";
import { CustomerType } from "./CustomerType";
import { ExistingSolar } from "./ExistingSolar";
import { HardStopScreen } from "./HardStopScreen";
import { RATProcessing } from "./RATProcessing";
import { RATResultsVerify } from "./RATResultsVerify";
import { Recommendation } from "./Recommendation";
import { RequestingQuote } from "./RequestingQuote";
import { RoofMaterial } from "./RoofMaterial";
import { ServiceProviderMatching } from "./ServiceProviderMatching";
import { SoftQuote } from "./SoftQuote";
import { ThreePhase } from "./ThreePhase";
import { UsagePattern } from "./UsagePattern";
import { SystemTypeAndAddress } from "./SystemTypeAndAddress";

export const customerType = {
  backRoute: () => Promise.resolve(`${screenNames.gettingStart}`),
  nextRoute: () => Promise.resolve(`../${screenNames.contactDetail}`),
};

export const CustomerTypeNavigator = () => (
  <CustomerType
    backRoute={customerType.backRoute}
    nextRoute={customerType.nextRoute}
  />
);

export const contactDetail = {
  backRoute: () => Promise.resolve(`../${screenNames.gettingStart}`),
  nextRoute: () => Promise.resolve(`../${screenNames.systemTypeAndAddress}`),
};

export const ContactDetailNavigator = () => (
  <ContactDetail
    backRoute={contactDetail.backRoute}
    nextRoute={contactDetail.nextRoute}
  />
);

/* istanbul ignore next */
export const ratProcessing = {
  nextRoute: () => Promise.resolve(`../${screenNames.ratResultsVerify}`),
  degradedNextRoute: () => Promise.resolve(`../${screenNames.roofMaterial}`),
  hardStopRoute: () => Promise.resolve(`../${screenNames.hardStop}`),
  delay: 2000,
};

/* istanbul ignore next */
export const RATProcessingNavigator = () => (
  <RATProcessing
    nextRoute={ratProcessing.nextRoute}
    degradedNextRoute={ratProcessing.degradedNextRoute}
    hardStopRoute={ratProcessing.hardStopRoute}
    delay={ratProcessing.delay}
  />
);

export const ratResultsVerify = {
  backRoute: () => Promise.resolve(`../${screenNames.systemTypeAndAddress}`),
  nextRoute: () => Promise.resolve(`../${screenNames.roofMaterial}`),
  nextExistingSolarRoute: () =>
    Promise.resolve(`../${screenNames.existingSolar}`),
};

export const RATResultsVerifyNavigator = () => (
  <RATResultsVerify
    backRoute={ratResultsVerify.backRoute}
    nextRoute={ratResultsVerify.nextRoute}
    nextExistingSolarRoute={ratResultsVerify.nextExistingSolarRoute}
  />
);

export const existingSolar = {
  backRoute: () => Promise.resolve(`../${screenNames.ratResultsVerify}`),
  nextRoute: () => Promise.resolve(`../${screenNames.roofMaterial}`),
};

export const ExistingSolarNavigator = () => (
  <ExistingSolar
    backRoute={existingSolar.backRoute}
    nextRoute={existingSolar.nextRoute}
  />
);

export const roofMaterial = {
  backRoute: () => Promise.resolve(`../${screenNames.ratResultsVerify}`),
  nextRoute: () => Promise.resolve(`../${screenNames.averageBill}`),
  degradedNextRoute: () =>
    Promise.resolve(`../${screenNames.serviceProviderMatching}`),
  degradedBackRoute: () =>
    Promise.resolve(`../${screenNames.systemTypeAndAddress}`),
  backExistingSolarRoute: () =>
    Promise.resolve(`../${screenNames.existingSolar}`),
  hardStopRoute: () => Promise.resolve(`../${screenNames.hardStop}`),
};

export const RoofMaterialNavigator = () => (
  <RoofMaterial
    backRoute={roofMaterial.backRoute}
    nextRoute={roofMaterial.nextRoute}
    degradedNextRoute={roofMaterial.degradedNextRoute}
    degradedBackRoute={roofMaterial.degradedBackRoute}
    backExistingSolarRoute={roofMaterial.backExistingSolarRoute}
    hardStopRoute={roofMaterial.hardStopRoute}
  />
);

export const averageBill = {
  backRoute: () => Promise.resolve(`../${screenNames.roofMaterial}`),
  nextRoute: () => Promise.resolve(`../${screenNames.usagePattern}`),
  nextGoInstallerRoute: () =>
    Promise.resolve(`../${screenNames.serviceProviderMatching}`),
};

export const AverageBillNavigator = () => (
  <AverageBill
    backRoute={averageBill.backRoute}
    nextRoute={averageBill.nextRoute}
    nextGoInstallerRoute={averageBill.nextGoInstallerRoute}
  />
);

export const solarGeneration = {
  batteryNextRoute: () => Promise.resolve(`../${screenNames.usagePattern}`),
  degradedBatteryRoute: () =>
    Promise.resolve(`../${screenNames.serviceProviderMatching}`),
  batteryBackRoute: () =>
    Promise.resolve(`../${screenNames.systemTypeAndAddress}`),
};

export const SolarGenerationNavigator = () => (
  <SolarGeneration
    batteryNextRoute={solarGeneration.batteryNextRoute}
    degradedBatteryRoute={solarGeneration.degradedBatteryRoute}
    batteryBackRoute={solarGeneration.batteryBackRoute}
  />
);

export const usagePattern = {
  backRoute: () => Promise.resolve(`../${screenNames.averageBill}`),
  nextRoute: () => Promise.resolve(`../${screenNames.threePhase}`),
  batteryBackRoute: () => Promise.resolve(`../${screenNames.solarGeneration}`),
};

export const UsagePatternNavigator = () => (
  <UsagePattern
    backRoute={usagePattern.backRoute}
    nextRoute={usagePattern.nextRoute}
    batteryBackRoute={usagePattern.batteryBackRoute}
  />
);

export const threePhase = {
  backRoute: () => Promise.resolve(`../${screenNames.usagePattern}`),
  nextRoute: () => Promise.resolve(`../${screenNames.recommendation}`),
  batteryNextRoute: () => Promise.resolve(`../${screenNames.batteryOutlook}`),
  solarBatteryNextRoute: () =>
    Promise.resolve(`../${screenNames.solarBatteryOutlook}`),
};

export const ThreePhaseNavigator = () => (
  <ThreePhase
    backRoute={threePhase.backRoute}
    nextRoute={threePhase.nextRoute}
    batteryNextRoute={threePhase.batteryNextRoute}
    solarBatteryNextRoute={threePhase.solarBatteryNextRoute}
  />
);

export const recommendation = {
  backRoute: () => Promise.resolve(`../${screenNames.threePhase}`),
  nextRoute: () => Promise.resolve(`../${screenNames.serviceProviderMatching}`),
  hardStopRoute: () => Promise.resolve(`../${screenNames.hardStop}`),
};

export const RecommendationNavigator = () => (
  <Recommendation
    backRoute={recommendation.backRoute}
    nextRoute={recommendation.nextRoute}
    hardStopRoute={recommendation.hardStopRoute}
  />
);

export const batteryOutlook = {
  batteryNextRoute: () =>
    Promise.resolve(`../${screenNames.serviceProviderMatching}`),
  batteryBackRoute: () => Promise.resolve(`../${screenNames.threePhase}`),
};

export const BatteryOutlookNavigator = () => (
  <BatteryOutlook
    batteryNextRoute={batteryOutlook.batteryNextRoute}
    batteryBackRoute={batteryOutlook.batteryBackRoute}
  />
);

export const solarBatteryOutlook = {
  nextRoute: () => Promise.resolve(`../${screenNames.serviceProviderMatching}`),
  backRoute: () => Promise.resolve(`../${screenNames.threePhase}`),
  hardStopRoute: () => Promise.resolve(`../${screenNames.hardStop}`),
};

export const SolarBatteryOutlookNavigator = () => (
  <SolarBatteryOutlook
    nextRoute={solarBatteryOutlook.nextRoute}
    backRoute={solarBatteryOutlook.backRoute}
    hardStopRoute={solarBatteryOutlook.hardStopRoute}
  />
);

export const serviceProviderMatching = {
  hardStopRoute: () => Promise.resolve(`../${screenNames.hardStop}`),
  nextRoute: () => Promise.resolve(`../${screenNames.softQuote}`),
  delay: 2000,
};

export const ServiceProviderMatchingNavigator = () => (
  <ServiceProviderMatching
    hardStopRoute={serviceProviderMatching.hardStopRoute}
    nextRoute={serviceProviderMatching.nextRoute}
    delay={serviceProviderMatching.delay}
  />
);

export const softQuote = {
  backRoute: () => Promise.resolve(`../${screenNames.recommendation}`),
  solarBatteryBackRoute: () =>
    Promise.resolve(`../${screenNames.solarBatteryOutlook}`),
  nextRoute: () => Promise.resolve(`../${screenNames.customerContact}`),
  batteryBackRoute: () => Promise.resolve(`../${screenNames.batteryOutlook}`),
  degradedBatteryBackRoute: () =>
    Promise.resolve(`../${screenNames.solarGeneration}`),
  degradedSolarBackRoute: () =>
    Promise.resolve(`../${screenNames.roofMaterial}`),
  goInstallerBackRoute: () => Promise.resolve(`../${screenNames.averageBill}`),
};

export const SoftQuoteNavigator = () => (
  <SoftQuote
    backRoute={softQuote.backRoute}
    nextRoute={softQuote.nextRoute}
    batteryBackRoute={softQuote.batteryBackRoute}
    solarBatteryBackRoute={softQuote.solarBatteryBackRoute}
    degradedBatteryBackRoute={softQuote.degradedBatteryBackRoute}
    degradedSolarBackRoute={softQuote.degradedSolarBackRoute}
    goInstallerBackRoute={softQuote.goInstallerBackRoute}
  />
);

export const customerContact = {
  backRoute: () => Promise.resolve(`../${screenNames.softQuote}`),
  nextRoute: () => Promise.resolve(`../${screenNames.requestingQuote}`),
};

export const CustomerContactNavigator = () => (
  <CustomerContact
    backRoute={customerContact.backRoute}
    nextRoute={customerContact.nextRoute}
  />
);

export const requestingQuote = {
  nextRoute: () => Promise.resolve(`../${screenNames.confirmation}`),
  hardStopRoute: () => Promise.resolve(`../${screenNames.hardStop}`),
  delay: 2000,
};

export const RequestingQuoteNavigator = () => (
  <RequestingQuote
    nextRoute={requestingQuote.nextRoute}
    hardStopRoute={requestingQuote.hardStopRoute}
    delay={requestingQuote.delay}
  />
);

export const systemTypeAndAddress = {
  backRoute: () => Promise.resolve(`../${screenNames.contactDetail}`),
  nextRoute: () => Promise.resolve(`../${screenNames.ratProcessing}`),
  hardStopRoute: () => Promise.resolve(`../${screenNames.hardStop}`),
  batteryNextRoute: () => Promise.resolve(`../${screenNames.solarGeneration}`),
};

export const SystemTypeAndAddressNavigator = () => (
  <SystemTypeAndAddress
    backRoute={systemTypeAndAddress.backRoute}
    nextRoute={systemTypeAndAddress.nextRoute}
    hardStopRoute={systemTypeAndAddress.hardStopRoute}
    batteryNextRoute={systemTypeAndAddress.batteryNextRoute}
  />
);

export const screenRoutes = [
  {
    path: screenNames.customerType,
    element: <CustomerTypeNavigator />,
  },
  {
    path: screenNames.contactDetail,
    element: <ContactDetailNavigator />,
  },
  {
    path: screenNames.roofMaterial,
    element: <RoofMaterialNavigator />,
  },
  {
    path: screenNames.systemTypeAndAddress,
    element: <SystemTypeAndAddressNavigator />,
  },
  {
    path: screenNames.solarGeneration,
    element: <SolarGenerationNavigator />,
  },
  {
    path: screenNames.batteryOutlook,
    element: <BatteryOutlookNavigator />,
  },
  {
    path: screenNames.customerContact,
    element: <CustomerContactNavigator />,
  },
  {
    path: screenNames.ratProcessing,
    element: <RATProcessingNavigator />,
  },
  {
    path: screenNames.ratResultsVerify,
    element: <RATResultsVerifyNavigator />,
  },
  {
    path: screenNames.existingSolar,
    element: <ExistingSolarNavigator />,
  },
  {
    path: screenNames.averageBill,
    element: <AverageBillNavigator />,
  },
  {
    path: screenNames.threePhase,
    element: <ThreePhaseNavigator />,
  },
  {
    path: screenNames.usagePattern,
    element: <UsagePatternNavigator />,
  },
  {
    path: screenNames.recommendation,
    element: <RecommendationNavigator />,
  },
  {
    path: screenNames.solarBatteryOutlook,
    element: <SolarBatteryOutlookNavigator />,
  },
  {
    path: screenNames.serviceProviderMatching,
    element: <ServiceProviderMatchingNavigator />,
  },
  {
    path: screenNames.softQuote,
    element: <SoftQuoteNavigator />,
  },
  {
    path: screenNames.requestingQuote,
    element: <RequestingQuoteNavigator />,
  },
  {
    path: screenNames.confirmation,
    element: <Confirmation />,
  },
  {
    path: screenNames.hardStop,
    element: <HardStopScreen />,
  },
];
